import { useEffect } from "react";
import React from "react";
import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Alert, Snackbar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { kebabCase } from "change-case";
import { Check } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import { RootState } from "../../app/store";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { downloadPromptsPackage } from "../../app/promptsSlice";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

function PromptsPackagesModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: any;
}) {
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = React.useState("");
  const [selectedPromptsPackage, setSelectedPromptsPackage] = React.useState("");
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const packages = [
    "College Students",
    "Content Creators",
    "Entrepreneurs",
    "High School Students",
    "high School Teachers",
    "HR",
    "Managers",
    "Marketers",
    "Parents",
    "Product Managers",
    "Project Managers",
    "Executive Assistants",
  ];

  const isDownloadingPromptsPackage = useAppSelector(
    (state: RootState) => state.prompts.isDownloadingPromptsPackage
  );

  useEffect(() => {
    if (open) {
    }
  }, [open]);

  const handleCloseSnackbar = () => {
    setErrorMessage("");
    setOpenSnackbar(false);
  };

  const handleSelectPackage = (promptsPackage: string) => {
    if (promptsPackage === selectedPromptsPackage) setSelectedPromptsPackage("");
    else setSelectedPromptsPackage(promptsPackage);
  };

  const handleDownloadSelectedPromptsPackage = () => {
    const selectedPromptsPackageSlug = kebabCase(selectedPromptsPackage);
    dispatch(downloadPromptsPackage(selectedPromptsPackageSlug))
      .unwrap()
      .then((promptMessages) => {
        onClose();
      })
      .catch((error) => {
        setErrorMessage(error);
        setOpenSnackbar(true);
      });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      disablePortal
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={openSnackbar && errorMessage !== ""}
          autoHideDuration={1000}
          onClose={handleCloseSnackbar}
          message=""
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
        <Box
          sx={{
            maxWidth: `calc(100vw - 16px)`,
            height: `calc(var(--unit-100vh) - 16px)`,
            width: 1200,
            bgcolor: "background.paper",
            border: "2px solid #000",
            borderRadius: 2,
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              pl: 2,
              pt: 1,
              pb: 1,
              borderBottom: "#000 1px solid",
              display: "flex",
            }}
          >
            <Box
              key="model-title"
              sx={{ display: "flex", flexGrow: 1, alignItems: "center" }}
            >
              <Typography variant="button">
                Start with one of our prompts packages:
              </Typography>
            </Box>
            <Box sx={{ mr: 1 }}>
              <IconButton color="inherit" edge="start" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              overflow: "auto",
              position: "relative",
              scrollbarColor: "gray transparent",
            }}
          >
            <Grid container spacing={5} sx={{ p: 1 }}>
              {packages.map((promptsPackage) => {
                const promptsPackageSlug = kebabCase(promptsPackage);
                return (
                  <Grid
                    component="button"
                    onClick={() => handleSelectPackage(promptsPackage)}
                    key={promptsPackageSlug}
                    size={{ xs: 12, md: 4 }}
                    sx={{
                      textAlign: "center",
                      position: "relative",
                      backgroundColor:
                        selectedPromptsPackage === promptsPackage
                          ? "rgb(249, 250, 251)"
                          : "#525252",
                      borderRadius: 2,
                      p: 2,
                      "& .package-text": {
                        color:
                          selectedPromptsPackage === promptsPackage
                            ? "#121212"
                            : "#fff",
                      },
                      "&:hover": {
                        backgroundColor: "rgb(249, 250, 251)",
                        cursor: "pointer",
                        "& .package-text": {
                          color: "#121212",
                        },
                      },
                      border: "none",
                    }}
                  >
                    {selectedPromptsPackage === promptsPackage && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          background: "blue",
                          color: "white",
                          height: 30,
                          width: 30,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "50%",
                        }}
                      >
                        <Check />
                      </Box>
                    )}
                    <Box
                      sx={{
                        height: 250,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        mb: 2,
                      }}
                    >
                      <img
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                        src={`/packages/${promptsPackageSlug}.svg`}
                        alt={`prompts for ${promptsPackage}`}
                      />
                    </Box>
                    <Typography variant="body1" className="package-text" fontWeight="bold">
                      PROMPTS FOR {promptsPackage.toUpperCase()}
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
          <Box
            sx={{
              p:1,
              borderTop: "#000 1px solid",
              display: "flex",
              justifyContent: "end",
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Button
                onClick={handleDownloadSelectedPromptsPackage}
                disabled={selectedPromptsPackage === "" || isDownloadingPromptsPackage}
                variant="contained"
                size="large"
                color="primary"
                fullWidth
              >
                {isDownloadingPromptsPackage ? (
                  <CircularProgress size={20} />
                ) : selectedPromptsPackage === "" ? (
                  "Select Prompt Package to Download"
                ) : (
                  `Start with prompts for ${selectedPromptsPackage}`
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default PromptsPackagesModal;
